import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

export interface CompareProduct {
  id: string;
  name: string;
  slug: string;
  category: string;
  price: number;
  currency?: string;
  description: string;
  image?: string;
  featured?: boolean;
  specifications?: Record<string, string>;
}

interface CompareContextValue {
  items: CompareProduct[];
  addItem: (product: CompareProduct) => void;
  removeItem: (productId: string) => void;
  clearItems: () => void;
  isInCompare: (productId: string) => boolean;
  canAddMore: boolean;
}

const STORAGE_KEY = 'siaedts-product-quote-list';
const MAX_COMPARE_ITEMS = 20;

const CompareContext = createContext<CompareContextValue | undefined>(undefined);

export function CompareProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CompareProduct[]>(() => {
    if (typeof window === 'undefined') {
      return [];
    }

    try {
      const saved = window.localStorage.getItem(STORAGE_KEY);
      return saved ? (JSON.parse(saved) as CompareProduct[]) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    }
  }, [items]);

  const addItem = (product: CompareProduct) => {
    setItems((current) => {
      if (current.some((item) => item.id === product.id)) {
        return current;
      }

      if (current.length >= MAX_COMPARE_ITEMS) {
        return current;
      }

      return [...current, product];
    });
  };

  const removeItem = (productId: string) => {
    setItems((current) => current.filter((item) => item.id !== productId));
  };

  const clearItems = () => {
    setItems([]);
  };

  const isInCompare = (productId: string) => items.some((item) => item.id === productId);

  const value = useMemo<CompareContextValue>(() => ({
    items,
    addItem,
    removeItem,
    clearItems,
    isInCompare,
    canAddMore: items.length < MAX_COMPARE_ITEMS,
  }), [items]);

  return <CompareContext.Provider value={value}>{children}</CompareContext.Provider>;
}

export function useCompareProducts() {
  const context = useContext(CompareContext);

  if (!context) {
    throw new Error('useCompareProducts must be used inside a CompareProvider');
  }

  return context;
}

export const compareConfig = {
  maxItems: MAX_COMPARE_ITEMS,
};
